import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { GripVertical, MapPin, Undo2 } from 'lucide-react';
import { useDispatchBoard } from '../../lib/dispatch-board.js';
import { jobSiteLine } from '../../lib/job-site.js';
import type { Trade } from '../../lib/trade.js';
import type { SalesOrderSummary, SalesType } from '../../lib/api.js';
import { TradePill } from '../ui/trade-pill.js';

interface QueueRailProps {
  salesTypes: ReadonlyArray<SalesType>;
  /** Orders that aren't placed on the timeline yet. */
  queue: ReadonlyArray<{ order: SalesOrderSummary; trade: Trade }>;
  /** Called when a card starts being dragged so the board can render drop zones. */
  onDragStart: (orderId: number, trade: Trade) => void;
  onDragEnd: () => void;
  /** Drop a placed card here to unschedule it (clears FOP_TECH / DATE / START / DURATION / COMPLETED_AT). */
  onUnassignDrop: (orderId: number) => void;
  /** An EXTRA visit dragged onto the rail: remove that visit only — the order keeps its own schedule. */
  onRemoveVisitDrop: (visitId: string) => void;
}

/**
 * Left-rail unassigned queue. Cards are native HTML5 draggables; the
 * orderId travels in dataTransfer text/plain and the board handles the
 * drop. Matches `.ib-rail` / `.uq-card` in the mockup.
 */
export function QueueRail({ salesTypes, queue, onDragStart, onDragEnd, onUnassignDrop, onRemoveVisitDrop }: QueueRailProps) {
  const filter = useDispatchBoard((s) => s.filter);
  const visible = queue.filter(({ trade }) => filter === 'all' || trade === filter);
  // True while a PLACED card is being dragged over the rail — drives the
  // green-tinted drop zone treatment. Queue-card drags (dropped back where
  // they came from) don't toggle this since their dragstart doesn't set
  // the source-slot mime; the drop handler's `sourceSlot` check filters
  // them out before calling onUnassignDrop.
  const [overWithPlaced, setOverWithPlaced] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    // Only show the unschedule treatment when a PLACED card is being
    // dragged (it carries the source-slot mime). DataTransfer.types is
    // available during dragover; reading payload values isn't until drop.
    if (e.dataTransfer.types.includes('application/x-tinsmith-source-slot')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setOverWithPlaced(true);
    }
  };
  const handleDragLeave = () => setOverWithPlaced(false);
  const handleDrop = (e: React.DragEvent) => {
    const sourceSlot = e.dataTransfer.getData('application/x-tinsmith-source-slot');
    setOverWithPlaced(false);
    if (sourceSlot === '') return; // drag came from the queue itself — no-op
    e.preventDefault();
    const visitId = e.dataTransfer.getData('application/x-tinsmith-visit');
    if (visitId !== '') {
      onRemoveVisitDrop(visitId);
      return;
    }
    const raw = e.dataTransfer.getData('text/plain');
    const orderId = Number(raw);
    if (Number.isFinite(orderId)) onUnassignDrop(orderId);
  };

  return (
    <aside
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      // h-full so the rail fills the wrapper's definite height — without
      // this, the aside collapses to its sticky-header's intrinsic height
      // and the inner overflow-y-auto has nothing to bound against.
      className="border-r flex flex-col h-full min-h-0 overflow-hidden transition-colors"
      style={{
        borderColor: 'var(--color-border-tertiary)',
        backgroundColor: overWithPlaced ? 'var(--feedback-success-bg)' : undefined,
        boxShadow: overWithPlaced ? 'inset 0 0 0 2px var(--feedback-success-border)' : undefined,
      }}
    >
      {/* Sticky header so the count stays visible even while the dispatcher
         scrolls a long queue. */}
      <div
        className="sticky top-0 z-10 flex items-center justify-between text-[10px] uppercase tracking-wider text-ink-subtle px-3 pt-3 pb-1.5 bg-surface border-b"
        style={{ borderColor: 'var(--color-border-tertiary)' }}
      >
        <span>Unassigned ({queue.length})</span>
        {visible.length !== queue.length && (
          <span className="text-ink-subtle/80 normal-case tracking-normal">
            {visible.length} shown
          </span>
        )}
      </div>
      {overWithPlaced && (
        <div
          className="px-3 py-1 text-[10.5px] flex items-center gap-1.5 border-b"
          style={{
            color: 'var(--feedback-success-fg)',
            borderColor: 'var(--feedback-success-border)',
            backgroundColor: 'var(--feedback-success-bg)',
          }}
          role="status"
        >
          <Undo2 className="h-3 w-3" aria-hidden />
          Release to unschedule
        </div>
      )}
      {/* Independently scrollable list — without overflow-y-auto here a long
         queue would push the board off-screen and the dispatcher couldn't
         reach cards at the bottom without losing their drop target. */}
      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2">
        <ul className="space-y-1.5">
          {visible.map(({ order, trade }) => (
            <QueueCard
              key={order.spireId}
              salesTypes={salesTypes}
              order={order}
              trade={trade}
              onDragStart={() => onDragStart(order.spireId, trade)}
              onDragEnd={onDragEnd}
            />
          ))}
        </ul>
        {visible.length === 0 && (
          <div className="text-[11px] text-ink-subtle">No matching unassigned work.</div>
        )}
        <p className="mt-3 text-[10.5px] text-ink-subtle leading-snug">
          Drag a card → onto a tech &amp; hour · click a name to open it
        </p>
      </div>
    </aside>
  );
}

function QueueCard({
  salesTypes,
  order,
  trade,
  onDragStart,
  onDragEnd,
}: {
  salesTypes: ReadonlyArray<SalesType>;
  order: SalesOrderSummary;
  trade: Trade;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const siteLine = jobSiteLine(order);
  return (
    <li
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(order.spireId));
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      className="rounded-md border bg-surface px-2.5 py-2 text-xs cursor-grab active:cursor-grabbing transition-shadow hover:shadow-sm"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="flex items-start gap-1.5">
        {/* Click opens the order — no need to schedule it first. The link
            itself is not draggable so a drag still moves the whole card. */}
        <Link
          to="/sales-orders/$id"
          params={{ id: String(order.spireId) }}
          draggable={false}
          className="group min-w-0 flex-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
          aria-label={`Open ${order.type === 'quote' ? 'quote' : 'order'} ${order.orderNo} — ${order.customer.name}`}
        >
          <div className="font-medium text-ink truncate flex items-center gap-1.5">
            <span className="truncate group-hover:underline">{order.customer.name}</span>
            {order.type === 'quote' && <QuoteBadge />}
          </div>
          <div className="text-[10.5px] text-ink-muted truncate">
            {order.orderNo} · ${order.total}
          </div>
          {siteLine && (
            <div className="text-[10.5px] text-ink-muted truncate flex items-center gap-1">
              <MapPin className="h-2.5 w-2.5 shrink-0" aria-hidden />
              <span className="truncate">{siteLine}</span>
            </div>
          )}
        </Link>
        <GripVertical className="h-3.5 w-3.5 shrink-0 text-ink-subtle" aria-hidden />
      </div>
      <div className="mt-1.5 flex items-center gap-1">
        <TradePill types={salesTypes} trade={trade} className="text-[9px] px-1 py-0.5 rounded" />
        <span className="text-[10px] text-ink-subtle">
          {order.requiredDate ? `req ${order.requiredDate.slice(5)}` : 'no date'}
        </span>
      </div>
    </li>
  );
}

function QuoteBadge() {
  // Distinct from trade badges — uses the warning palette so dispatchers
  // immediately see "this is a quote, not a committed order." Assigning a
  // quote still writes the FOP_TECH UDF; the order stays type=Q until a
  // future explicit promote action flips it to W.
  return (
    <span
      className="inline-block rounded-full text-[9px] font-medium px-1.5 py-0.5 shrink-0"
      style={{
        backgroundColor: 'var(--pill-sales-bg)',
        color: 'var(--pill-sales-fg)',
      }}
    >
      Quote
    </span>
  );
}

